import { Image, useFocusableGroup } from '@fluentui/react-components';
import { IconNames } from '@office-start/fabric-icon-subset-utility';
import IconButtonControl from '@office-start/icon-button-control';
import { useGlobalTheme } from '@office-start/theming-service';
import { useSingletonWindowSize } from '@office-start/window-utility';
import classNames from 'classnames';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import styles from './TaskModuleDialogControl.module.scss';
import { getHeight, getWidth } from './utils';
const TaskModuleDialogControl = forwardRef(({ children, taskInfo, onCancel, appName, icon, hideHeader, locStrings }, ref) => {
    // TODO: look into using ResizeObserver for the window element
    const { height: windowHeight, width: windowWidth } = useSingletonWindowSize();
    const [sizes, setSizes] = useState({
        height: getHeight(windowHeight, taskInfo.height),
        width: getWidth(windowWidth, taskInfo.width),
    });
    // TODO: adaptive card doesn't have an icon, figure out where to get this from
    const iconSrc = icon ||
        'https://static2.sharepointonline.com/files/fabric-cdn-prod_20200430.002/assets/brand-icons/product/png/office_48x2.png';
    const iconDimensions = useGlobalTheme().fontSizeHero900;
    const dialogRef = useRef(null);
    const focusAttributes = useFocusableGroup({
        tabBehavior: 'limited-trap-focus',
    });
    useImperativeHandle(ref, () => ({
        set: ({ height, width }) => {
            height = getHeight(windowHeight, height);
            width = getWidth(windowWidth, width);
            setSizes({ height, width });
        },
    }));
    useEffect(() => {
        setSizes({
            height: getHeight(windowHeight, taskInfo.height),
            width: getWidth(windowWidth, taskInfo.width),
        });
        // windowHeight and windowWidth here are not directly used in the this useEffect hook but needed because
        // it is used within the getHeight function that is called to assign the height and width of the sizes object
    }, [windowHeight, windowWidth, taskInfo.height, taskInfo.width]);
    /**
     * The sizes object here, both height and width, is added to handle the initial case with useSingletonWindowSize
     * returns 0 before re-rendering with the correct window height and width.
     * see {@link https://office.visualstudio.com/OC/_git/office-start?path=/packages/utilities/window-utility/src/useSingletonWindowSize.ts&version=GBmain&line=4&lineEnd=4&lineStartColumn=1&lineEndColumn=46&lineStyle=plain&_a=contents}
     */
    if (!taskInfo || sizes.height === 0 || sizes.width === 0) {
        return null;
    }
    return (React.createElement("div", Object.assign({ className: styles['task-module-page-overlay'] }, focusAttributes),
        React.createElement("div", { className: classNames('ms-bgc-nd', styles['task-module-page-overlay__background']) }),
        React.createElement("div", { ref: dialogRef, style: {
                minWidth: sizes.width,
                minHeight: sizes.height,
            }, className: classNames('ms-bgc-w', hideHeader
                ? styles['task-module-dialog-no-header']
                : styles['task-module-dialog']), "data-testid": hideHeader
                ? 'task-module-dialog-no-header'
                : 'task-module-dialog', role: "dialog", "aria-modal": "true", "aria-label": taskInfo.title },
            React.createElement("div", { className: classNames('ms-fcl-nd', styles['task-module-dialog-dismiss-x']) },
                React.createElement(IconButtonControl, { iconName: IconNames.ChromeClose, onClick: onCancel, tabIndex: 0, title: locStrings.closeIconText, iconClass: "ms-fcl-nd" })),
            !hideHeader && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classNames(styles['task-module-dialog-header']) },
                    React.createElement(Image, { className: classNames(styles['task-module-dialog__header-icon']), 
                        // TODO: fix header image falling outside of smaller sized dialogs
                        src: iconSrc, alt: locStrings.applicationLogoAlt, height: iconDimensions, width: iconDimensions }),
                    React.createElement("span", { id: "task-module-dialog__header-text", className: classNames('ms-fcl-ns', styles['task-module-dialog__header-text']) },
                        React.createElement("h3", { id: "task-module-dialog__app-name", className: classNames('ms-fcl-ns', styles['task-module-dialog__app-name']) }, appName),
                        React.createElement("span", { id: "task-module-dialog__task-title", className: classNames('ms-fcl-ns', styles['task-module-dialog__task-title']) }, taskInfo.title))))),
            React.createElement("div", { className: classNames('ms-fcl-nd', hideHeader
                    ? styles['task-module-dialog-content-no-header']
                    : styles['task-module-dialog-content']) },
                React.createElement("div", { className: styles['task-module-mos-app-container-extender'] }, children)))));
});
TaskModuleDialogControl.displayName = 'TaskModuleDialogControl';
export { TaskModuleDialogControl };
//# sourceMappingURL=TaskModuleDialogControl.js.map